import api from './api';
import type { Booking } from '@/types';

// Description: Get all bookings from client context state
// Response: Array<Booking>
export const getBookings = async (contracts: any[] = [], params?: { unitNumber?: string; contractId?: number; year?: number; week?: number }) => {
  // If no contracts exist yet, return empty array
  if (!contracts || contracts.length === 0) {
    return [];
  }

  // Get all bookings from contracts
  let allBookings: Booking[] = contracts.flatMap(contract => contract.bookings || []);

  // Filter bookings based on params if provided
  if (params) {
    if (params.unitNumber) {
      allBookings = allBookings.filter(b => b.unitNumber === params.unitNumber);
    }
    if (params.contractId) {
      allBookings = allBookings.filter(b => b.contractId === params.contractId);
    }
    if (params.year) {
      const year = params.year;
      allBookings = allBookings.filter(b => b.years.includes(year));
    }
    if (params.week) {
      allBookings = allBookings.filter(b => b.week === params.week);
    }
  }

  return allBookings;
};

// Description: Get bookings by client ID from context state
export const getBookingsByClient = async (contracts: any[], contractId: number): Promise<Booking[]> => {
  // Find the contract with the given ID
  const contract = contracts.find(c => c._id === parseInt(contractId as unknown as string));

  // If contract is found and has bookings, return those bookings
  if (contract && Array.isArray(contract.bookings)) {
    // Make sure each booking has the contractId field set
    return contract.bookings.map((booking: Booking) => ({
      ...booking,
      contractId: parseInt(contractId as unknown as string)
    }));
  }

  // If no contract is found or it has no bookings, return an empty array
  return [];
};

// Description: Delete a booking
// Endpoint: DELETE /clc/bookings/:id
// Request: { bookingId: number }
// Response: { success: boolean, message: string }
export const deleteBooking = async (bookingId: number): Promise<{ success: boolean, message: string }> => {
  try {
    const response = await api.delete(`/api/clc/bookings/${bookingId}`);
    return response.data;
  } catch (error: any) {
    console.error('Error deleting booking:', error);

    // Handle 404 Not Found
    if (error.response?.status === 404) {
      // console.warn(`Booking ID ${bookingId} not found. Treating as already deleted.`);
      return { success: true, message: "Booking deleted" };
    }

    // Return the error response data if available
    if (error.response?.data) {
      return error.response.data;
    }

    throw new Error(error?.response?.data?.message || error.message);
  }
};

// Description: Create bookings for a contract
// Endpoint: POST /clc/bookings
// Request: { contractId: number, bookings: Array<{ unitDetailId: number, week: number, years: number[], status: string, isCustom: boolean }> }
// Response: { success: boolean, message: string, bookings: Array<Booking> }
export const createBookings = async (contractId: number, bookings: Omit<Booking, '_id' | 'contractId'>[]) => {
  try {
    const formattedBookings = bookings.map((booking: any) => ({
      unitNumber: booking.unitNumber,
      resort: booking.resort,
      type: booking.type,
      week: Number(booking.week),
      years: booking.years,
      status: 'pending',
      isCustom: booking.isCustom || false,
      externalAvailabilityId: booking.externalAvailabilityId,
    }));

    const response = await api.post('/api/clc/bookings', {
      contractId: contractId,
      bookings: formattedBookings
    });
    
    return response.data;
  } catch (error: any) {
    console.error('Error creating bookings:', error);
    
    // Return the error response data if available
    if (error.response?.data) {
      return error.response.data;
    }
    
    if (error.response && error.response.data) {
      throw new Error(error.response.data.message || JSON.stringify(error.response.data));
    }
    throw new Error(error.message);
  }
};

// Description: Create custom bookings for a contract
// Endpoint: POST /clc/bookings
// Request: { contractId: number, bookings: Array<Booking> }
// Response: { success: boolean, message: string, bookings: Array<Booking> }
export const createCustomBookings = async (contractId: number, bookings: Omit<Booking, '_id'>[]) => {
  try {
    // Format the bookings for the API
    const formattedBookings = bookings.map(booking => ({
      unitNumber: booking.unitNumber || '',
      resort: booking.resort || '',
      type: booking.type || '',
      location: booking.location || '',
      week: Number(booking.week), // Convert week to number explicitly
      years: booking.years,
      status: 'pending', // Always set status to 'pending' as per user requirement
      isCustom: true
    }));

    // Call the API endpoint for bookings
    const response = await api.post('/api/clc/bookings', {
      contractId: contractId,
      bookings: formattedBookings
    });

    return response.data;
  } catch (error: any) {
    console.error('Error creating custom bookings:', error);
    
    // Return the error response data if available
    if (error.response?.data) {
      return error.response.data;
    }
    
    if (error.response && error.response.data) {
      throw new Error(error.response.data.message || JSON.stringify(error.response.data));
    }
    throw new Error(error.message);
  }
};